import React, { useEffect, useState } from 'react';
import {
    Box, Chip, IconButton, MenuItem, Select, TextField, Tooltip, Typography
} from '@mui/material';
import { red, orange, amber, blue, purple, grey, green } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import Datatable from '../../components/Datatable';
import api from '../../services/api';
import { useAuth } from '../../components/AuthProvider';
import { useNotify } from '../../components/Datatable/NotifyProvider';

const CAUSE_COLORS = {
    'Timeout':           red[600],
    'Sem rota':          orange[700],
    'DNS':               amber[700],
    'Conexão recusada':  purple[600],
    'Credencial':        red[800],
    'SSH':               blue[600],
    'Template/Comando':  purple[800],
    'Outras':            grey[500],
};

export default function ErroBackup() {
    const auth = useAuth();
    const notify = useNotify();
    const [refresh, setRefresh] = useState(false);
    const [solved, setSolved] = useState('open');
    const [cause, setCause] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [causeOptions, setCauseOptions] = useState([]);


    useEffect(() => {
        document.title = 'IPV7 Hermes - Erros de Backup';
        api.get('/hermes/log_backup/causes')
            .then(r => setCauseOptions(r.data?.causes || []))
            .catch(() => {});
    }, []);


    return (
        <Datatable
            refresh={refresh}
            handlerRefresh={() => setRefresh(!refresh)}
            title="Erros de Backup"
            endpoint="/hermes/log_backup/filtered"
            filters={{
                solved,
                cause: cause || null,
                date_from: dateFrom || null,
                date_to: dateTo || null,
            }}
            options={{
                customToolbar: () => (
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                        <Select size="small" value={solved} onChange={(e) => { setSolved(e.target.value); setRefresh(!refresh); }}
                                sx={{ minWidth: 130 }}>
                            <MenuItem value="open">Não resolvidos</MenuItem>
                            <MenuItem value="resolved">Resolvidos</MenuItem>
                            <MenuItem value="all">Todos</MenuItem>
                        </Select>
                        <Select size="small" value={cause} displayEmpty
                                onChange={(e) => { setCause(e.target.value); setRefresh(!refresh); }}
                                sx={{ minWidth: 150 }}>
                            <MenuItem value="">Todas as causas</MenuItem>
                            {causeOptions.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                        <TextField type="date" size="small" label="De"
                                   value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                   InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                        <TextField type="date" size="small" label="Até"
                                   value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                   InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                        <Tooltip title="Atualizar">
                            <IconButton size="small" onClick={() => setRefresh(!refresh)}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
                customToolbarSelect: () => '',
            }}
            columns={[
                { name: 'id', label: 'ID', options: { display: false, filter: false, sort: false } },
                { name: 'host_id', label: 'Host ID', options: { display: false, filter: false, sort: false } },
                { name: 'ip_address', label: 'IP', options: { display: false } },
                { name: 'vendor', label: 'Vendor', options: { display: false } },
                { name: 'type', label: 'Tipo', options: { display: false } },
                { name: 'message', label: 'Mensagem (raw)', options: { display: false } },
                { name: 'solved', label: 'Resolvido?', options: { display: false } },
                {
                    name: 'host_name', label: 'Host',
                    options: {
                        customBodyRender: (value, tableMeta) => {
                            // 0=id, 1=host_id, 2=ip, 3=vendor, 4=type, 5=msg, 6=solved, 7=host_name
                            const ip = tableMeta.rowData[2];
                            const vendor = tableMeta.rowData[3];
                            const type = tableMeta.rowData[4];
                            return (
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>{value}</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">{ip}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                        {vendor} — {type}
                                    </Typography>
                                </Box>
                            );
                        },
                    },
                },
                {
                    name: 'cause', label: 'Causa / Mensagem',
                    options: {
                        sort: false,
                        customBodyRender: (value, tableMeta) => {
                            const msg = tableMeta.rowData[5];
                            const color = CAUSE_COLORS[value] || grey[500];
                            return (
                                <Box>
                                    <Chip label={value} size="small"
                                          sx={{ bgcolor: color, color: 'white', fontWeight: 600, height: 20 }} />
                                    {msg && (
                                        <Tooltip title={msg}>
                                            <Typography variant="caption" color="text.secondary"
                                                        sx={{ display: 'block', mt: 0.3, maxWidth: 360 }}
                                                        noWrap>
                                                {msg}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        },
                    },
                },
                {
                    name: 'created_at', label: 'Quando',
                    options: {
                        customBodyRender: (v) => (
                            <span>{moment(v).locale('pt-br').format('DD/MM/YYYY HH:mm:ss')}</span>
                        ),
                    },
                },
                {
                    name: 'actions', label: 'Ações',
                    options: {
                        filter: false, sort: false,
                        customBodyRender: (_, tableMeta) => {
                            const isSolved = !!tableMeta.rowData[6];
                            if (isSolved) {
                                return (
                                    <Chip icon={<CheckCircleIcon />} label="resolvido" size="small"
                                          sx={{ bgcolor: green[50], color: green[700] }} />
                                );
                            }
                            return (
                                <Tooltip title="Resolve automaticamente quando o próximo backup rodar com sucesso">
                                    <Chip label="pendente" size="small"
                                          sx={{ bgcolor: red[50], color: red[700] }} />
                                </Tooltip>
                            );
                        },
                    },
                },
            ]}
        />
    );
}
